import type { ChangeEvent } from 'react';
import type { ContentTypeDefinition } from '../../../lib/flotiq-client/types/api-response';
import type { DropdownOption } from '../../Dropdown/DropdownOption/DropdownOption';
import type { HeaderFilterUpdateFiltersFn } from '../HeaderFilterDatasource/HeaderFilterDatasource';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { GridRenderFilterEvent } from '../../../lib/flotiq-plugins/plugin-events/GridRenderFilterEvent';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import useDebounceCallback from '../../../hooks/useDebounceCallback';
import usePluginResults from '../../../hooks/usePluginResults';

import CheckCircle from '../../CheckCircle/CheckCircle';
import Dropdown from '../../Dropdown/Dropdown';
import ElementFromPlugin from '../../ElementFromPlugin/ElementFromPlugin';
import Input from '../../Input/Input';
import HeaderFilterDatasource from '../HeaderFilterDatasource/HeaderFilterDatasource';

export type DataGridHeaderFilter = {
  [key: string]: { type?: string | number; filter: string | string[] };
};

export interface HeaderFilterProps {
  /**
   * HeaderFilter id
   */
  id?: string;
  /**
   * HeaderFilter input type
   */
  filterInputType?: string;
  /**
   * HeaderFilter handle filter function
   */
  handleFilters?: HeaderFilterUpdateFiltersFn;
  /**
   * HeaderFilter value
   */
  value?: any;
  /**
   * Current filters
   */
  currentFilters?: DataGridHeaderFilter;
  /**
   * HeaderFilter options
   */
  options?: DropdownOption[] | string[];
  /**
   * HeaderFilter grid testid
   */
  testId?: string;
  /**
   * HeaderFilter dropdown open left position
   */
  dropdownLeftPosition?: boolean;
  /**
   * HeaderFilter validation
   */
  validation?: {
    relationContenttype?: string;
    relationMultiple?: boolean;
  };
  /**
   * HeaderFilter set to true to disable filter
   */
  disabled?: boolean;
  /**
   * Content type for datasource filter
   */
  contentType?: ContentTypeDefinition | null;
  /**
   * Extra options for datasource filter
   */
  extraOptions?: DropdownOption[];
  /**
   * HeaderFilter callback to get filtered data from
   */
  filterDatasourceFetch?: (
    query?: string,
  ) => Promise<DropdownOption[]> | undefined;
  /**
   * HeaderFilter dropdown multiple
   */
  multiple?: boolean;
}

const HeaderFilter = ({
  id = '',
  filterInputType,
  handleFilters,
  value = '',
  currentFilters,
  options = EMPTY_ARRAY,
  testId = '',
  dropdownLeftPosition = false,
  validation = EMPTY_OBJECT,
  disabled = false,
  extraOptions,
  filterDatasourceFetch,
  contentType,
  multiple = false,
}: HeaderFilterProps) => {
  const { t } = useTranslation();
  let inputComponent;
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleFilterDebounced = useDebounceCallback(
    (id: string, e: ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value);
      handleFilters?.(id, e.target.value);
    },
    500,
  );

  /**
   * @emits FlotiqPlugins."flotiq.grid.filter::render"
   */
  const results = usePluginResults(
    'flotiq.grid.filter::render',
    GridRenderFilterEvent,
    {
      inputType: filterInputType,
      accessor: id,
      updateFilters: handleFilters,
      allFilters: currentFilters,
      disabled,
      contentType,
    },
  );

  if (results && results.length > 0) {
    inputComponent = <ElementFromPlugin results={results} />;
  } else if (
    filterInputType === 'select' ||
    filterInputType === 'checkbox' ||
    filterInputType === 'radio'
  ) {
    let dropdownOptions = options;

    if (filterInputType === 'checkbox')
      dropdownOptions = [
        {
          searchString: t('Global.True'),
          label: (
            <div className="flex flex-row justify-center items-center">
              <CheckCircle checked={true} />
              <span className="ml-2">{t('Global.True')}</span>
            </div>
          ),
          value: 'true',
        },
        {
          searchString: t('Global.False'),
          label: (
            <div className="flex flex-row justify-center items-center">
              <CheckCircle checked={false} />
              <span className="ml-2">{t('Global.False')}</span>
            </div>
          ),
          value: 'false',
        },
      ];

    inputComponent = (
      <Dropdown
        onChange={(e) => handleFilterDebounced(id, e)}
        options={dropdownOptions?.map((el) =>
          typeof el === 'string' ? { value: el, label: el } : el,
        )}
        value={value}
        nullable={true}
        additionalContainerClasses={'h-10 min-h-10'}
        additionalClasses={'relative w-full flex flex-col'}
        additionalDropdownClasses={`w-auto min-w-full top-12 ${
          dropdownLeftPosition ? 'left-0' : 'right-0'
        }`}
        disabled={disabled}
        multiple={multiple}
        maxVisibleValues={1}
        maxVisibleValuesText="..."
        {...getTestProps(testId, `${id}-select`, 'testId')}
      />
    );
  } else if (filterInputType === 'datasource') {
    inputComponent = (
      <HeaderFilterDatasource
        id={id}
        handleFilters={handleFilterDebounced}
        value={value}
        dropdownLeftPosition={dropdownLeftPosition}
        validation={validation}
        disabled={disabled}
        filterDatasourceFetch={filterDatasourceFetch}
        extraOptions={extraOptions}
        {...getTestProps(testId, `${id}-datasource`, 'testId')}
      />
    );
  }
  // Case:  add text type input as default only when filterInputType was defined.
  // For the 'action' or 'selection' column, there is no need to add a default input filter
  else if (filterInputType) {
    inputComponent = (
      <Input
        onChange={(e) => handleFilterDebounced(id, e)}
        additionalInputClasses={'h-10'}
        value={currentValue}
        disabled={disabled}
        type="search"
        isSearchIcon={false}
        {...getTestProps(testId, `${id}-text`, 'testId')}
      />
    );
  }

  return (
    /**
     * This wrapper only stops click propagation to the sortable header,
     * its not intended for user interaction purposes,
     * so disabling sonar rule for accessibility here is fine.
     */
    <div /* NOSONAR */
      {...getTestProps(testId, id)}
      className={twMerge(
        'header-filter',
        'flex order-2 absolute bottom-0 left-0',
        'cursor-default p-[10px] w-full border-t border-slate-100 dark:border-slate-800',
        'h-16 font-normal bg-slate-50 dark:bg-slate-950',
      )}
      onClick={(e) => e.stopPropagation()} // Prevent triggered sort on header
    >
      {inputComponent}
    </div>
  );
};

export default HeaderFilter;
